import Vue from "vue";
import productDetails from "@/data/products";
import { calcTotalPrice } from "@/helpers/pricing";

const STORAGE_KEY = "nanome-cart";
const STORAGE_LAST_KEY = "nanome-last-cart";
const saved = localStorage.getItem(STORAGE_KEY);

const state = {
  items: {},
  maxItems: 30
};

if (saved) {
  try {
    // restore saved cart, remove unused keys, and fix NaNs
    const realKeys = Object.values(productDetails.available);
    Object.assign(state.items, JSON.parse(saved));

    const newKeys = Object.keys(state.items);
    for (const key of newKeys) {
      if (!realKeys.includes(key)) {
        delete state.items[key];
      } else if (isNaN(state.items[key])) {
        state.items[key] = 0;
      }
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
  } catch (err) {
    localStorage.removeItem(STORAGE_KEY);
  }
}

// Send cart data to integromat
const sendCartData = email => {
  if (!getters.totalProducts(state)) return;
  if (!navigator.sendBeacon) return;

  // don't send if cart not modified
  const lastCart = localStorage.getItem(STORAGE_LAST_KEY);
  if (JSON.stringify(state.items) == lastCart) return;
  localStorage.setItem(STORAGE_LAST_KEY, JSON.stringify(state.items));

  const data = new FormData();
  data.append("email", email);

  // Filter out items in cart that aren't available
  const realKeys = Object.values(productDetails.available);
  const cartKeys = Object.keys(state.items);

  const items = { ...state.items };
  cartKeys.forEach(key => {
    if (!realKeys.includes(key)) {
      delete items[key];
    }
  });

  const query = "?" + new URLSearchParams(items);
  const url = "https://home.nanome.ai/cart" + query;
  data.append("url", url);

  const hook = "https://hook.integromat.com/tgy2y4lejvkn4118uv1row396ba8itnc";
  navigator.sendBeacon(hook, data);
};

const getters = {
  totalProducts(state) {
    return Object.values(state.items).reduce((a, x) => a + x, 0);
  },

  summary(state, getters) {
    return getters.products.map(product => {
      if (state.items[product.id]) {
        const details = productDetails[product.type];
        const type = details.bundleOf ? "bundle" : "license";
        const count = state.items[product.id];
        const name = details.bundleName || details.name;
        return `${count} ${name} ${type}${count === 1 ? "" : "s"}`;
      }
    }, []);
  },

  products(state, getters, rootState) {
    return rootState.products.filter(product => state.items[product.id]);
  },

  subtotal(state, getters) {
    if (!getters.products.length) return 0;

    return getters.products.reduce((acc, cur) => {
      if (!cur.amount && !cur.tiers) return acc;
      return acc + calcTotalPrice(cur, state.items[cur.id]);
    }, 0);
  }
};

const mutations = {
  SET_COUNT(state, { id, count }) {
    Vue.set(state.items, id, Math.max(0, Math.min(state.maxItems, count)));
  }
};

const actions = {
  addProductCount({ commit, dispatch, state }, { id, count }) {
    let newCount = count;
    if (state.items[id] !== undefined) {
      newCount += state.items[id];
    }
    commit("SET_COUNT", { id, count: newCount });
    dispatch("saveCart");
  },

  setProductCount({ commit, dispatch }, { id, count }) {
    commit("SET_COUNT", { id, count });
    dispatch("saveCart");
  },

  clearCart({ state, commit, dispatch }) {
    for (const id in state.items) {
      commit("SET_COUNT", { id, count: 0 });
    }
    dispatch("saveCart");
  },

  saveCart({ state }) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
  },

  sendCartData(context, email) {
    if (process.env.NODE_ENV === "production" && email) {
      sendCartData(email);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
